import { useCallback, useEffect, useState } from 'react';

import { ApiError, readApiError } from 'utils/apiError';

/**
 * Five wrong codes lock the account for fifteen minutes. The backend
 * sends `retryAfterMinutes` as a number precisely so we do not have to
 * read it back out of a translated sentence — use it to stop the admin
 * hammering a button that cannot succeed.
 *
 * There is deliberately no manual clear: the lockout is on the ACCOUNT,
 * so switching input, reopening the modal or reloading does not lift
 * it. Only the timer does.
 */
const useTwoFaLockout = () => {
  const [lockedMinutes, setLockedMinutes] = useState(0);

  useEffect(() => {
    if (!lockedMinutes) {
      return;
    }

    const timer = setTimeout(
      () => setLockedMinutes(0),
      lockedMinutes * 60 * 1000
    );

    return () => clearTimeout(timer);
  }, [lockedMinutes]);

  /** Read the error, and start the lockout when the backend reports one. */
  const readError = useCallback((err: unknown): ApiError => {
    const error = readApiError(err);

    if (error.code === 'TWO_FA_LOCKED' && error.retryAfterMinutes) {
      setLockedMinutes(error.retryAfterMinutes);
    }

    return error;
  }, []);

  return { isLocked: lockedMinutes > 0, readError };
};

export default useTwoFaLockout;
